/**
 * ConversationHeader - Top bar showing friend info in active conversation
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation } from '../types/chat';
import { FriendshipTier } from '../../friends/types/friends';

// Tier colors matching FriendCard
const TIER_COLORS: Record<FriendshipTier, string> = {
  'TIER_1': 'text-yellow-500',
  'TIER_2': 'text-gray-400',
  'TIER_3': '', // Custom color #A75217 applied via inline style
  'BASIC': 'text-gray-300'
};

interface ConversationHeaderProps {
  conversation: Conversation;
  onClose?: () => void; // For mobile/dock view
}

export const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  conversation,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleAvatarClick = () => {
    navigate(`/friends/${conversation.friendUserId}`);
  };

  const getTierColor = (tier?: string): string => {
    if (!tier) return '';
    return TIER_COLORS[tier as FriendshipTier] || '';
  };

  const getTierStyle = (tier?: string): React.CSSProperties | undefined => {
    if (tier === 'TIER_3') {
      return { color: '#A75217' };
    }
    return undefined;
  };

  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        {/* Avatar (clickable) */}
        <button
          onClick={handleAvatarClick}
          className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
        >
          {conversation.friendAvatar ? (
            <img
              src={conversation.friendAvatar}
              alt={conversation.friendName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-sm text-gray-600">
                {conversation.friendName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </button>

        {/* Friend info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {conversation.friendName}
            </h3>
            {conversation.friendTier && (
              <span 
                className={`text-base ${getTierColor(conversation.friendTier) || ''}`}
                style={getTierStyle(conversation.friendTier)}
                title={conversation.friendTier}
              >
                ★
              </span>
            )}
          </div>
          {conversation.friendUsername && (
            <p className="text-sm text-gray-500 truncate">@{conversation.friendUsername}</p>
          )}
        </div>

        {/* View Profile button */}
        <button
          onClick={handleAvatarClick}
          className="flex-shrink-0 text-sm text-blue-500 hover:text-blue-600 font-medium"
        >
          View Profile
        </button>
      </div>

      {/* Close button (mobile/dock) */}
      {onClose && (
        <button
          onClick={onClose}
          className="ml-4 p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Close conversation"
        >
          <svg
            className="w-5 h-5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

